import type { ReactNode } from "react";
import { ArrowUpRight, Sparkles, TrendingDown, TrendingUp } from "lucide-react";
import { cn } from "@/lib/cn";
import type { Insight } from "@/mock";

/** Pulsing coral live indicator (design.md: coral allowed on the live dot). */
export function LiveDot({ label, className }: { label?: ReactNode; className?: string }) {
  return (
    <span className={cn("inline-flex items-center gap-2", className)}>
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-70" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
      </span>
      {label && <span className="text-[13px] text-muted">{label}</span>}
    </span>
  );
}

/** Nara's mark — a warm coral disc with a spark. Nara is a colleague, not a bot. */
export function NaraAvatar({ size = "md", className }: { size?: "sm" | "md" | "lg"; className?: string }) {
  const dims = { sm: "h-7 w-7", md: "h-9 w-9", lg: "h-12 w-12" }[size];
  const ic = { sm: "h-3.5 w-3.5", md: "h-4 w-4", lg: "h-5 w-5" }[size];
  return (
    <span
      className={cn(
        "grid shrink-0 place-items-center rounded-full text-white shadow-[0_4px_14px_-4px_rgba(184,68,34,0.6)]",
        dims,
        className,
      )}
      style={{ background: "radial-gradient(120% 120% at 30% 20%, #d4592f, #B84422 55%, #8f3015)" }}
      aria-label="Nara"
    >
      <Sparkles className={ic} strokeWidth={1.75} />
    </span>
  );
}

/**
 * A Nara insight — the entry point to the see→make→measure loop. "Ask Nara to
 * fix" hands the suggestion to Make with the prompt prefilled.
 */
export function InsightCard({
  insight,
  onAsk,
  className,
}: {
  insight: Insight;
  onAsk?: () => void;
  className?: string;
}) {
  const Trend = insight.severity === "down" ? TrendingDown : insight.severity === "up" ? TrendingUp : Sparkles;
  return (
    <div
      className={cn(
        "rounded-card border border-hairline bg-paper p-4 shadow-sm dark:shadow-none",
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <NaraAvatar size="sm" />
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex items-center gap-2">
            <span className="eyebrow !text-accent">{insight.metric}</span>
            <Trend className="h-3.5 w-3.5 text-accent" strokeWidth={2} />
          </div>
          <h4 className="text-[14px] font-semibold leading-snug text-ink-strong">{insight.title}</h4>
          <p className="mt-1 text-[13px] leading-relaxed text-muted">{insight.detail}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              onClick={onAsk}
              className="inline-flex items-center gap-1.5 rounded-btn bg-accent px-3 py-1.5 text-[13px] font-medium text-white transition-colors hover:bg-accent-hover"
            >
              Ask Nara to fix
              <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
            </button>
            {insight.page && (
              <span className="font-mono text-[11px] text-subtle">{insight.page}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
